import { useState, useEffect, useCallback } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Container, Text, Stack, Select, Group, Divider, Modal, Button } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { ResponsiveTitle } from '../../components/ResponsiveTitle/ResponsiveTitle'
import { isAdminAuthenticated, refreshAdminSession } from '../../lib/auth'
import { AdminNavbar } from '../../components/AdminNavbar/AdminNavbar'
import { PhotoGallery } from '../../components/PhotoGallery/PhotoGallery'
import { PhotoLightbox } from '../../components/PhotoLightbox/PhotoLightbox'
import { usePhotos } from '../../hooks/usePhotos'
import { useGuests } from '../../hooks/useGuests'
import type { PhotoForDisplay } from '../../lib/types'

export const Route = createFileRoute('/admin/gallery')({
  component: AdminGallery,
})

function AdminGallery() {
  const navigate = useNavigate()
  const { fetchAllPhotos, deletePhoto } = usePhotos()
  const { guests } = useGuests()

  const [lightboxOpened, setLightboxOpened] = useState(false)
  const [lightboxPhotos, setLightboxPhotos] = useState<PhotoForDisplay[]>([])
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [selectedGuestId, setSelectedGuestId] = useState<string | null>(null)
  const [galleryRefreshKey, setGalleryRefreshKey] = useState(0)
  const [deleteModalOpened, setDeleteModalOpened] = useState(false)
  const [photoToDelete, setPhotoToDelete] = useState<PhotoForDisplay | null>(null)

  useEffect(() => {
    // Check if admin is authenticated
    if (!isAdminAuthenticated()) {
      navigate({ to: '/admin/login' })
      return
    }

    // Refresh session on page load
    refreshAdminSession()

    // Set up interval to check session expiration
    const interval = setInterval(() => {
      if (!isAdminAuthenticated()) {
        navigate({ to: '/admin/login' })
      }
    }, 60000) // Check every minute

    return () => clearInterval(interval)
  }, [navigate])

  const handlePhotoClick = useCallback(
    async (photo: PhotoForDisplay, index: number) => {
      // Load all photos for lightbox navigation
      const result = await fetchAllPhotos({
        page: 1,
        pageSize: 'all',
        guestId: selectedGuestId || undefined,
        sortBy: 'uploaded_at',
        sortDirection: 'desc',
      })
      setLightboxPhotos(result.photos)
      // Find the index of clicked photo in the full list
      const fullIndex = result.photos.findIndex((p) => p.id === photo.id)
      setLightboxIndex(fullIndex >= 0 ? fullIndex : index)
      setLightboxOpened(true)
    },
    [fetchAllPhotos, selectedGuestId]
  )

  const handleDeleteClick = useCallback((photo: PhotoForDisplay) => {
    setPhotoToDelete(photo)
    setDeleteModalOpened(true)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (!photoToDelete) return

    try {
      const success = await deletePhoto(photoToDelete.id, photoToDelete.storagePath)

      if (success) {
        notifications.show({
          title: 'Erfolgreich gelöscht',
          message: 'Das Foto wurde erfolgreich gelöscht.',
          color: 'green',
        })

        // Refresh gallery
        setGalleryRefreshKey((prev) => prev + 1)
      } else {
        notifications.show({
          title: 'Fehler',
          message: 'Foto konnte nicht gelöscht werden.',
          color: 'red',
        })
      }
    } catch (error) {
      console.error('Delete error:', error)
      notifications.show({
        title: 'Fehler',
        message: 'Ein unerwarteter Fehler ist aufgetreten.',
        color: 'red',
      })
    } finally {
      setDeleteModalOpened(false)
      setPhotoToDelete(null)
    }
  }, [photoToDelete, deletePhoto])

  const handleGuestFilterChange = useCallback((value: string | null) => {
    setSelectedGuestId(value || null)
    setGalleryRefreshKey((prev) => prev + 1) // Refresh gallery
  }, [])

  // Prepare guest options for filter dropdown
  const guestOptions = [
    { value: '', label: 'Alle Gäste' },
    ...guests.map((guest) => ({
      value: guest.id,
      label: guest.primary_name,
    })),
  ]

  return (
    <div>
      <AdminNavbar />
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <ResponsiveTitle order={1}>Foto-Galerie</ResponsiveTitle>
          <Text size="lg" c="dimmed">
            Hier können Sie alle von Gästen hochgeladenen Fotos einsehen und verwalten.
          </Text>

          <Divider />

          {/* Guest Filter */}
          <Group justify="flex-start">
            <Select
              label="Nach Gast filtern"
              placeholder="Alle Gäste"
              value={selectedGuestId || ''}
              onChange={handleGuestFilterChange}
              data={guestOptions}
              clearable
              style={{ width: 250 }}
            />
          </Group>

          {/* Admin Photo Gallery */}
          <AdminPhotoGallery
            key={galleryRefreshKey}
            guestFilter={selectedGuestId || undefined}
            onPhotoClick={handlePhotoClick}
            onDeleteClick={handleDeleteClick}
          />

          {/* Photo Lightbox */}
          <PhotoLightbox
            opened={lightboxOpened}
            onClose={() => setLightboxOpened(false)}
            photos={lightboxPhotos}
            initialIndex={lightboxIndex}
            showGuestName={true}
          />

          {/* Delete Confirmation Modal */}
          <Modal
            opened={deleteModalOpened}
            onClose={() => {
              setDeleteModalOpened(false)
              setPhotoToDelete(null)
            }}
            title="Foto löschen"
            centered
            aria-labelledby="admin-delete-modal-title"
          >
            <Stack gap="md">
              {photoToDelete && (
                <>
                  <Text>
                    Möchten Sie dieses Foto wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                  </Text>
                  {photoToDelete.guestName && (
                    <Text size="sm" c="dimmed">
                      Gast: {photoToDelete.guestName}
                    </Text>
                  )}
                  <Text size="sm" c="dimmed">
                    Datei: {photoToDelete.filename}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Hochgeladen: {photoToDelete.uploadedAt.toLocaleString('de-DE')}
                  </Text>
                </>
              )}
              <Group justify="flex-end" mt="md">
                <Button
                  variant="subtle"
                  onClick={() => {
                    setDeleteModalOpened(false)
                    setPhotoToDelete(null)
                  }}
                  autoFocus
                >
                  Abbrechen
                </Button>
                <Button color="red" onClick={handleConfirmDelete}>
                  Löschen
                </Button>
              </Group>
            </Stack>
          </Modal>
        </Stack>
      </Container>
    </div>
  )
}

// Admin-specific PhotoGallery wrapper component
interface AdminPhotoGalleryProps {
  guestFilter?: string
  onPhotoClick?: (photo: PhotoForDisplay, index: number) => void
  onDeleteClick?: (photo: PhotoForDisplay) => void
}

function AdminPhotoGallery({
  guestFilter,
  onPhotoClick,
  onDeleteClick,
}: AdminPhotoGalleryProps) {
  const { fetchAllPhotos, loading } = usePhotos()
  const [photos, setPhotos] = useState<PhotoForDisplay[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number | 'all'>(24)
  const [totalPages, setTotalPages] = useState(1)

  // Fetch photos when filter, page, or pageSize changes
  useEffect(() => {
    const loadPhotos = async () => {
      const result = await fetchAllPhotos({
        page,
        pageSize,
        guestId: guestFilter,
        sortBy: 'uploaded_at',
        sortDirection: 'desc',
      })

      setPhotos(result.photos)
      setTotalCount(result.totalCount)
      setTotalPages(result.totalPages)
    }

    loadPhotos()
  }, [guestFilter, page, pageSize, fetchAllPhotos])

  return (
    <PhotoGallery
      onPhotoClick={onPhotoClick}
      onDeleteClick={onDeleteClick}
      showDeleteButton={true}
      showGuestName={true}
      photos={photos}
      loading={loading}
      totalCount={totalCount}
      page={page}
      pageSize={pageSize}
      totalPages={totalPages}
      onPageChange={setPage}
      onPageSizeChange={(value) => {
        const newPageSize = value === 'all' ? 'all' : parseInt(value || '24', 10)
        setPageSize(newPageSize)
        setPage(1)
      }}
    />
  )
}

